<!-- src/components/admin/cards/CardPreviewDialog.vue -->
<template>
  <v-card class="rounded-xl">
    <v-sheet color="secondary" dark class="pa-4 d-flex align-center">
      <v-icon large class="mr-3">mdi-eye</v-icon>
      <div class="flex-grow-1">
        <div class="text-h6 font-weight-bold">{{ $t('Prévisualisation carte') }}</div>
        <div class="text-caption">{{ formatCode(card.code) }}</div>
      </div>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-sheet>
    
    <v-card-text class="pt-6 d-flex flex-column align-center">
      <!-- Carte physique mockup -->
      <div class="physical-card elevation-8">
        <!-- Header -->
        
        <!-- QR Code au centre -->
        <div class="card-qr-container">
          <canvas ref="qrCanvas" class="card-qr"></canvas>
        </div>
        
        <!-- Footer -->
        <div class="card-footer">
          <div class="card-code">{{ formatCodeFull(card.code) }}</div>
          <div class="card-subtitle">{{ $t('Carte de fidélité') }}</div>
        </div>
      </div>
      
      <!-- Informations -->
      <v-card outlined class="mt-4 full-width">
        <v-card-text class="pa-3">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption grey--text">{{ $t('Statut') }}</div>
              <v-chip x-small :color="statusColor(card.status)" text-color="white">
                {{ statusLabel(card.status) }}
              </v-chip>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text">{{ $t('État') }}</div>
              <v-chip x-small :color="card.is_active ? 'success' : 'grey'" text-color="white">
                {{ card.is_active ? $t('Active') : $t('Inactive') }}
              </v-chip>
            </v-col>
            <v-col cols="12" v-if="card.user">
              <div class="text-caption grey--text">{{ $t('Utilisateur') }}</div>
              <div class="font-weight-medium text-capitalize">
                {{ card.user.fullname || card.user.username }}
              </div>
            </v-col>
            <v-col cols="12" v-if="card.assigned_at">
              <div class="text-caption grey--text">{{ $t('Attribuée le') }}</div>
              <div>{{ formatDateTime(card.assigned_at) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>
    
    <v-divider />
    
    <v-card-actions class="pa-4 justify-center">
      <v-btn outlined color="primary" @click="downloadQR">
        <v-icon left>mdi-download</v-icon>
        {{ $t('Télécharger QR') }}
      </v-btn>
      <v-btn color="primary" @click="$emit('close')">
        <v-icon left>mdi-close</v-icon>
        {{ $t('Fermer') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: "CardPreviewDialog",
  
  props: {
    card: {
      type: Object,
      required: true
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.generateQR()
    })
  },
  
  methods: {
    formatCode(code) {
      return code ? code.substring(0, 8).toUpperCase() : '-'
    },
    
    formatCodeFull(code) {
      if (!code) return '-'
      // Format: XXXX-XXXX-XXXX-XXXX (premiers 16 caractères)
      const str = code.toString().substring(0, 16).toUpperCase()
      return str.match(/.{1,4}/g)?.join('-') || str
    },
    
    formatDateTime(datetime) {
      if (!datetime) return '-'
      const d = new Date(datetime)
      return d.toLocaleString('fr-FR')
    },
    
    statusColor(status) {
      return {
        available: "success",
        assigned: "primary",
        lost: "warning",
        blocked: "error",
      }[status] || "grey"
    },
    
    statusLabel(status) {
      return {
        available: this.$t("Disponible"),
        assigned: this.$t("Assignée"),
        lost: this.$t("Perdue"),
        blocked: this.$t("Bloquée"),
      }[status] || status
    },
    
    async generateQR() {
      try {
        const canvas = this.$refs.qrCanvas
        if (!canvas) return
        
        await QRCode.toCanvas(canvas, this.card.code, {
          width: 200,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
      } catch (e) {
        console.error('Erreur génération QR:', e)
      }
    },
    
    async downloadQR() {
      try {
        const canvas = this.$refs.qrCanvas
        if (!canvas) return
        
        const dataUrl = canvas.toDataURL('image/png')
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `carte_${this.formatCode(this.card.code)}.png`
        link.click()
        
        this.$store.commit("app/message", {
          msg: this.$t("QR code téléchargé"),
          color: "success"
        })
      } catch (e) {
        console.error('Erreur téléchargement QR:', e)
      }
    },
  },
}
</script>

<style scoped>
.physical-card {
  width: 320px;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: white;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title {
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 2px;
}

.card-qr-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.card-qr {
  display: block;
  width: 140px !important;
  height: 140px !important;
}

.card-footer {
  text-align: center;
  margin-top: auto;
}

.card-code {
  font-size: 11px;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
  opacity: 0.9;
  margin-bottom: 4px;
}

.card-subtitle {
  font-size: 10px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.full-width {
  width: 100%;
}
</style>